import React, { ReactElement, Ref, useCallback } from 'react';

import PropTypes from 'prop-types';

import { stripUnknownProps } from '@adsk/alloy-react-helpers';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme } from '@adsk/alloy-react-styles';
import {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import ToggleButton from './ToggleButton';
import getAriaProps from '../../helpers/getAriaProps';
import { GetToggleButtonProps, GenericItem, DataAttributes } from '../../types';

type ControlContainerProps<TItem extends GenericItem> =
  StylableComponent<HTMLDivElement> &
    DataAttributes & {
      getToggleButtonProps: GetToggleButtonProps;
      disabled?: boolean;
      isLoading?: boolean;
      error?: boolean;
      isOpen: boolean;
      isAutocomplete?: boolean;
      highDensity?: boolean;
      highlightButtonOnFocus?: boolean;
      temp?: TItem;
    };

const ControlContainerWrapped = <TItem extends GenericItem>(
  {
    className,
    style,
    disabled = false,
    isLoading = false,
    error,
    isOpen = false,
    getToggleButtonProps,
    isAutocomplete,
    children,
    highDensity,
    highlightButtonOnFocus,
    onClick,
    ...props
  }: ControlContainerProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const { tokens } = useTheme();
  const controlTokens = tokens?.component.dropdown?.control;
  const iconTokens = controlTokens?.icon;

  const ariaProps = getAriaProps(props);
  const ariaPropsKeys = Object.keys(ariaProps);

  // if this is not autocomplete, the ref gets provided to getToggleButtonProps to merge them
  const {
    ref: divRef,
    onClick: divOnClick,
    ...divProps
  } = isAutocomplete
    ? { ref }
    : getToggleButtonProps({
        // TODO: remove this cast once this is merged https://github.com/downshift-js/downshift/pull/1568
        ref: ref as Ref<HTMLButtonElement>,
        disabled,
        // Pass aria props to the toggle button for proper accessibility
        ...ariaProps,
      });

  // Strip unused props from container div.
  // For non-autocomplete: also strip aria props since they're now on the toggle button.
  // For autocomplete: keep aria props so they can flow to children (SearchInput via getInputProps).
  const controlLabelProps = stripUnknownProps(props, [
    'selectItem',
    'selectedItem',
    'itemToString',
    'openMenu',
    ...(isAutocomplete ? [] : ariaPropsKeys),
  ]);

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        onClick?.(event);
        divOnClick?.(event);
      }
    },
    [disabled, divOnClick, onClick],
  );

  const handleOnMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // for autocomplete components, prevent focus loss when clicking on the container
      if (isAutocomplete) {
        const target = event.target as HTMLElement;
        const isInputElement = target.tagName === 'INPUT';
        const isButtonElement = target.tagName === 'BUTTON';

        if (!isInputElement && !isButtonElement) {
          event.preventDefault();

          const container = event.currentTarget;
          const inputElement = container.querySelector('input');

          if (inputElement) {
            inputElement.focus();
          }
        }
      }
    },
    [isAutocomplete],
  );

  return (
    // This will be fixed in https://jira.autodesk.com/browse/DSGN-1832
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={divRef as Ref<HTMLDivElement>}
      data-testid={'Dropdown__control'}
      aria-disabled={disabled ? true : undefined}
      className={className}
      css={[
        {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          outline: 'none',
          ...controlTokens?._,
          ...getInteractiveStyles(disabled),
          ...(isOpen && controlTokens?.focused),
          ...(disabled && controlTokens?.disabled),
          ...(error && !disabled && controlTokens?.error),
        },
        style,
      ]}
      onClick={onClickHandler}
      onMouseDown={handleOnMouseDown}
      {...divProps}
      {...controlLabelProps}
    >
      {children}
      {isLoading && (
        <ProgressRing
          size={ProgressRing.SIZES.XSMALL}
          style={{
            marginLeft: highDensity ? 0 : iconTokens?._?.['margin-left'],
          }}
        />
      )}
      {isAutocomplete ? (
        <ToggleButton
          isOpen={isOpen}
          disabled={disabled}
          getToggleButtonProps={getToggleButtonProps}
          tabIndex={-1}
          style={{
            marginLeft: highDensity ? 0 : iconTokens?._?.['margin-left'],
            marginRight: iconTokens?._?.['margin-right'],
            ...(disabled && { color: iconTokens?.disabled?.color }),
          }}
          highlightButtonOnFocus={highlightButtonOnFocus}
        />
      ) : (
        <div
          style={{
            height: 32,
            maxWidth: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid={'Control__toggle'}
        >
          <ChevronDownIcon
            style={[
              {
                maxHeight: 32,
                maxWidth: 36,
                color: iconTokens?._?.color,
                marginLeft: highDensity ? 0 : iconTokens?._?.['margin-left'],
                marginRight: iconTokens?._?.['margin-right'],
                ...(isOpen && {
                  transform: 'rotate(180deg)',
                }),
                ...(disabled && {
                  color: iconTokens?.disabled?.color,
                }),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

const ControlContainer = React.forwardRef(ControlContainerWrapped);

ControlContainer.displayName = 'ControlContainer';

ControlContainer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** The content of te ControlContainer */
  children: PropTypes.any,
};

export default Object.assign(
  ControlContainer as <TItem extends GenericItem>(
    p: ControlContainerProps<TItem> & {
      ref?: Ref<HTMLDivElement>;
    },
  ) => ReactElement,
  { propTypes: ControlContainer.propTypes },
);
